import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IncentivePlanRecalculationStatus, SeasonPlanRepopulation } from '@coin/shared/merit-incentive/util';
import { HttpHelpersService, LoadingService } from '@coin/shared/core/data-access';
import { SeasonTypeEnum } from '@coin/shared/core/util-enums';
import { environment } from '@coin/shared/core/util-environments';
import { checkResult, encodeSeasonType, pollingStrategy, RuleEngineHelper } from '@coin/shared/core/util-helpers';
import {
  GenericSimulationResponse,
  MassExportMetadata,
  PaginatedResult,
  RuleSetV2,
  SeasonPlan,
  SeasonPlanSettings,
  TransactionStatus,
  TransactionStatusMetadata
} from '@coin/shared/core/util-models';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, retryWhen, tap } from 'rxjs/operators';
import { SeasonsIncentiveAdminService } from '@coin/shared/core/data-access-v2';

@Injectable({
  providedIn: 'root'
})
export class SeasonPlanService {
  private readonly httpClient = inject(HttpClient);
  private readonly toast = inject(ToastrService);
  private readonly loadingService = inject(LoadingService);
  private readonly httpHelper = inject(HttpHelpersService);
  private readonly translate = inject(TranslateService);
  private readonly seasonsIncentiveAdminService = inject(SeasonsIncentiveAdminService);

  public getSeasonPlans(seasonId: string, seasonType: SeasonTypeEnum): Observable<SeasonPlan[]> {
    const params = new HttpParams({ fromObject: { seasonId } });

    return this.httpClient
      .get<
        PaginatedResult<SeasonPlan>
      >(`${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/plans`, { params })
      .pipe(
        map(({ content }) => content),
        this.httpHelper.handleError('Cannot get season plans'),
        this.loadingService.withLoadingScreen
      );
  }

  public getPlanById(seasonType: SeasonTypeEnum, planId: string): Observable<SeasonPlan> {
    return this.httpClient
      .get<SeasonPlan>(`${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/plans/${planId}`)
      .pipe(map(checkResult()), retryWhen(pollingStrategy()), this.httpHelper.handleError('Cannot get plan'));
  }

  public createPlan(seasonType: SeasonTypeEnum, body: SeasonPlan): Observable<SeasonPlan> {
    this.loadingService.present();

    return this.httpClient
      .post<SeasonPlan>(`${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/plans`, body)
      .pipe(
        tap(() => {
          this.toast.success(`"${body.title}" created.`);
        }),
        this.httpHelper.handleError('Cannot create season plan'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  // used for updating plan without setting isInMonitoring
  public updatePlan(seasonType: SeasonTypeEnum, plan: SeasonPlan): Observable<SeasonPlan> {
    this.loadingService.present();

    return this.httpClient
      .patch<SeasonPlan>(`${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/plans/${plan.id}`, {
        ...plan,
        isInMonitoring: undefined
      })
      .pipe(
        this.httpHelper.handleError('Cannot update'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public createPlanSettings(
    seasonType: SeasonTypeEnum,
    planId: string,
    attachedDocumentUri: string
  ): Observable<SeasonPlanSettings> {
    if (seasonType === SeasonTypeEnum.IncentiveV2) {
      return this.seasonsIncentiveAdminService
        .seasonsIncentiveAdminCreatePlanSettings({ planId, createPlanSettings: { attachedDocumentUri } })
        .pipe(
          this.httpHelper.handleError('Error creating plan settings'),
          this.httpHelper.handleSuccess('Plan settings created')
        );
    }

    return this.httpClient
      .post<SeasonPlanSettings>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/season-plans/${planId}/settings`,
        {
          attachedDocumentUri
        }
      )
      .pipe(
        tap(() =>
          this.toast.success(this.translate.instant('season-mgmt.incentive.plan-management.link-document-selection'))
        ),
        this.httpHelper.handleError('Error creating plan settings')
      );
  }

  public deletePlanSettings(seasonType: SeasonTypeEnum, planId: string, settingsId: string): Observable<void> {
    if (seasonType === SeasonTypeEnum.IncentiveV2) {
      return this.seasonsIncentiveAdminService
        .seasonsIncentiveAdminDeletePlanSettings({ planId, id: settingsId })
        .pipe(
          this.httpHelper.handleError('Error deleting plan settings'),
          this.httpHelper.handleSuccess('Plan settings deleted')
        );
    }

    return this.httpClient
      .delete<void>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/season-plans/${planId}/settings/${settingsId}`
      )
      .pipe(
        tap(() =>
          this.toast.success(this.translate.instant('season-mgmt.incentive.plan-management.unlink-document-selection'))
        ),
        this.httpHelper.handleError('Error deleting plan settings')
      );
  }

  public getSimulatedPopulation(
    seasonType: SeasonTypeEnum,
    body: RuleSetV2,
    take: number,
    skip: number,
    planId: string
  ): Observable<GenericSimulationResponse> {
    const page = !skip ? 1 : Math.floor(skip / take) + 1;

    return this.httpClient
      .post<GenericSimulationResponse>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/plans/${planId}/simulate-population?page=${page}&size=${take}`,
        RuleEngineHelper.ruleSetV2ToDto(body)
      )
      .pipe(this.httpHelper.handleError('Cannot get simulated population'));
  }

  public savePopulation(
    seasonType: SeasonTypeEnum,
    planId: string
  ): Observable<TransactionStatus<TransactionStatusMetadata>> {
    this.loadingService.present();

    return this.httpClient
      .post<TransactionStatus<TransactionStatusMetadata>>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/plans/${planId}/save-population`,
        {
          id: planId
        }
      )
      .pipe(
        this.httpHelper.handleError('Cannot save population'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public deletePlan(seasonType: SeasonTypeEnum, planId: string): Observable<unknown> {
    return this.httpClient
      .delete(`${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/plans/${planId}`)
      .pipe(
        tap(() => {
          this.toast.success(`Deleted.`);
        }),
        this.httpHelper.handleError('Cannot delete season plan'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public getRecalculationStatus(): Observable<IncentivePlanRecalculationStatus[]> {
    return this.httpClient
      .get<
        IncentivePlanRecalculationStatus[]
      >(`${environment.api.baseUrl}/admin/api/v2/master/seasons/population-status`)
      .pipe(
        catchError((res: HttpErrorResponse) => {
          return throwError(`Error getting status. ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }

  public recalculate(body: SeasonPlanRepopulation): Observable<unknown> {
    return this.httpClient.post(`${environment.api.baseUrl}/admin/api/v2/master/seasons/repopulation`, body).pipe(
      tap(() => this.toast.success('Recalculation started.')),
      this.httpHelper.handleError('Cannot start recalculation')
    );
  }

  public setMonitoringState(plan: SeasonPlan, isInMonitoring: boolean): Observable<SeasonPlan> {
    this.loadingService.present();

    return this.httpClient
      .patch<SeasonPlan>(`${environment.api.baseUrl}/admin/api/v2/master/season-plans/${plan.id}`, {
        ...plan,
        isInMonitoring
      })
      .pipe(
        tap(() => {
          this.toast.success(isInMonitoring ? 'Monitoring Set!' : 'Monitoring Reset!');
        }),
        this.httpHelper.handleError('Cannot set state'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public setDesignReady(plan: SeasonPlan, isDesignReady: boolean): Observable<SeasonPlan> {
    this.loadingService.present();

    return this.httpClient
      .patch<SeasonPlan>(`${environment.api.baseUrl}/admin/api/v2/master/season-plans/${plan.id}`, {
        ...plan,
        isDesignReady,
        isInMonitoring: undefined
      })
      .pipe(
        tap(() => (isDesignReady ? this.toast.success('Design ready.') : this.toast.info('Design not ready.'))),
        this.httpHelper.handleError('Cannot set design state'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public recalculatePlans(seasonType: SeasonTypeEnum, seasonId: string): Observable<TransactionStatus> {
    this.loadingService.present();
    return this.httpClient
      .post<TransactionStatus>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/plans/seasons/${seasonId}/population-recalculation`,
        null
      )
      .pipe(
        this.httpHelper.handleError('Cannot recalculate population'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public startUnassignedValuesExport(
    seasonType: SeasonTypeEnum,
    seasonId: string
  ): Observable<TransactionStatus<MassExportMetadata>> {
    this.loadingService.present();
    return this.httpClient
      .put<
        TransactionStatus<MassExportMetadata>
      >(`${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/plans/missing-individual-valuess?seasonId=${seasonId}`, {})
      .pipe(
        this.httpHelper.handleError('Cannot export missing individual values'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public updatePlanPriorities(seasonId: string, changes: { priority: number; id: string }[]): Observable<unknown> {
    return this.loadingService.withLoadingScreen(
      this.httpClient
        .patch(`${environment.api.baseUrl}/admin/api/v2/master/season-plans/${seasonId}/priorities`, { changes })
        .pipe(this.httpHelper.handleError('Cannot update priorities'))
    );
  }

  public changePlanForEmployee(seasonId: string, employeeId: string, body: { planId: string }): Observable<unknown> {
    return this.httpClient
      .patch(
        `${environment.api.baseUrl}/admin/api/v2/master/seasons/merit/partner/${seasonId}/allocations/${employeeId}/change-scheme `,
        body
      )
      .pipe(this.httpHelper.handleError('Cannot change employee plan'), this.loadingService.withLoadingScreen);
  }
}
